import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { BottomSection, Box, SectionDiv, Title } from './styles';
import AddIconDropdown from '../../viewCard/addIconDropdown';
import { ProjectModel } from '../../../mobx/models/project';
import { dispatchProjectSuccess, updateProjectData } from '../../../services/projectServices';
import { ProjectUpdatePayloadInterface } from '../../../mobx/interfaces/project';
import Input from '../../input';
import { useMobileDevice } from '../../../global/useMobile';

/**
 * Props interface for ProjectInfo component
 * @property {ProjectModel} currentProject - The current project to display and edit info for
 */
interface Props {
  currentProject: ProjectModel;
}

const ProjectInfoCard: React.FC<Props> = observer(({ currentProject }) => {
  // State variables
  const [projectName, setProjectName] = useState(currentProject?.name);
  const [localIdentifier, setLocalIdentifier] = useState(currentProject?.identifier || '');
  // other variables
  const isMobile = useMobileDevice();

  // useEffect for Name update
  useEffect(() => {
    setProjectName(currentProject?.name);
  }, [currentProject?.name]);
  // useEffect for Identifier update
  useEffect(() => {
    setLocalIdentifier(currentProject?.identifier || '');
  }, [currentProject?.identifier]);

  // Update name when focus change and enter key pressed from identifier field
  const handleBlur = useCallback(async () => {
    setProjectName(projectName.trim());
    if (projectName.trim() !== currentProject?.name.trim()) {
      const payload: ProjectUpdatePayloadInterface = {
        Name: projectName.trim()
      };
      const response = await updateProjectData(currentProject?.id, payload);
      if (response) {
        currentProject?.setName(projectName.trim());
        dispatchProjectSuccess('Project Name updated successfully');
      }
    }
  }, [currentProject, projectName]);

  // Update identifier when focus change and enter key pressed from identifier field
  const handleIdentifierBlur = useCallback(async () => {
    const newIdentifier = localIdentifier.toUpperCase().trim();
    const currentIdentifier = (currentProject?.identifier || '').toUpperCase().trim();

    if (newIdentifier === currentIdentifier) {
      return;
    }

    const payload: ProjectUpdatePayloadInterface = {
      Identifier: newIdentifier
    };
    const response = await updateProjectData(currentProject?.id, payload);
    if (!response) {
      setLocalIdentifier(currentIdentifier);
    } else {
      currentProject?.setIdentifier(newIdentifier);
      dispatchProjectSuccess('Project Identifier updated successfully');
    }
  }, [currentProject, localIdentifier]);

  // function for update on enter key press
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  }, []);

  return (
    <Box>
      <SectionDiv>
        <Title>Icon & Name</Title>
        <BottomSection>
          <AddIconDropdown size={isMobile ? 38 : 36} currentProject={currentProject!} />
          <Input
            fieldWrapperWidth='100%'
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          />
        </BottomSection>
      </SectionDiv>
      <Input
        label={
          <Title>
            Identifier <span>(used in task IDs)</span>
          </Title>
        }
        value={localIdentifier}
        onChange={(e) => {
          const value = e.target.value.toUpperCase();
          if (/^[A-Z0-9]*$/.test(value)) {
            setLocalIdentifier(value);
          }
        }}
        onBlur={handleIdentifierBlur}
        onKeyDown={handleKeyDown}
        maxLength={7}
      />
    </Box>
  );
});

export default ProjectInfoCard;
